/* eslint-disable no-nested-ternary */
/* eslint-disable camelcase */
import { useState, FC } from 'react';
import Drawer from '@base/Drawer/Drawer';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import EnterEmail from '@cont/Settings/EnterEmail';
import { useSelectorToStore, useDispatchToStore } from '@hooks/reduxHooks';
import {
  sendMailOtp,
  verifyMailOtp,
  fetchMyInfo,
  resetNetworkCallsError
} from '@actions/auth';
import styled from '@emotion/styled';
import { Spacings, Fonts } from '@styles/index';
import H3 from '@base/H3/H3';
import H5 from '@base/H5/H5';
import H6 from '@base/H6/H6';
import CheckCircleFilled from '@icon/CheckCircleFilled';
import Button from '@base/Button/Button';
import { subscribeToExternalApp } from '@actions/checkout';
import { AnyArgVoidReturnFunction } from 'types/utils';
import { useRouter } from 'next/router';

const Wrapper = styled.div`
  grid-column: span 10;
  margin-bottom: ${Spacings.SPACING_24B};
`;

const Subtitle = styled(P1)`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_9B};
`;

const EmailVerifiedBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_6B};
  border: 1px solid var(--color-divider);
  border-radius: 4px;
  margin-bottom: ${Spacings.SPACING_10B};
`;

const EmailDisabledBlock = styled(EmailVerifiedBlock)`
  background-color: var(--color-base-0);
  border: none;
  margin-top: ${Spacings.SPACING_8B};
  margin-bottom: ${Spacings.SPACING_4B};
`;

const VerifiedBlock = styled.div`
  display: flex;
  align-items: center;
`;

const VerifiedText = styled(H5)`
  margin-left: ${Spacings.SPACING_2B};
`;

const ActivatedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HelpLine = styled.span`
  font-size: 14px;
  line-height: 150%;
  color: var(--color-text-primary);
`;

const ContactText = styled.span`
  font-weight: ${Fonts.BOLD};
  font-size: 14px;
  line-height: 100%;
  color: var(--color-i-green);
  padding-bottom: 4px;
  border-bottom: dashed 1px var(--color-text-primary);
  cursor: pointer;
  margin-left: ${Spacings.SPACING_1B};
`;

interface IExternalAppActivationTypes {
  showModal: boolean;
  closeModal: AnyArgVoidReturnFunction;
  onActivationComplete: AnyArgVoidReturnFunction;
  onClickStartLearning: AnyArgVoidReturnFunction;
  goalUID: string;
  dataAnalytics: string;
  purchaseSubscription: {
    [key: string]: any;
  };
}

const ExternalAppActivation: FC<IExternalAppActivationTypes> = ({
  showModal,
  closeModal,
  onActivationComplete,
  onClickStartLearning,
  goalUID,
  dataAnalytics,
  purchaseSubscription
}) => {
  const ButtonsWrapper = styled.div`
    display: flex;
    margin-top: ${Spacings.SPACING_10B};
    button:first-child {
      margin-right: ${Spacings.SPACING_4B};
    }
  `;
  const { pathname } = useRouter();
  const [fieldsData, setFieldsData] = useState({
    email: { type: 'email', value: '' },
    emailOtp: { type: 'number', value: '', show: false }
  });
  const props = useSelectorToStore(({ auth, checkout }) => {
    const { sendEmailOtpStatus, verifyEmailOtpStatus, me } = auth;
    const { purchaseSubscriptionResponse, externalAppData } = checkout;
    return {
      sendEmailOtpStatus,
      verifyEmailOtpStatus,
      purchaseSubscriptionResponse,
      externalAppData,
      me
    };
  });
  const dispatch = useDispatchToStore();

  const {
    sendEmailOtpStatus,
    verifyEmailOtpStatus,
    // purchaseSubscriptionResponse,
    externalAppData,
    me
  } = props;
  const [emailVerified, setEmailVerified] = useState(me.isEmailVerified);
  const [isActivated, setIsActivated] = useState(false);

  const handleEmailSubmit = async () => {
    const resp: any = await dispatch(sendMailOtp(fieldsData.email.value));
    if (resp.result) {
      setFieldsData({
        ...fieldsData,
        emailOtp: { ...fieldsData.emailOtp, show: true }
      });
    }
  };
  const handleEmailOtpSubmit = async () => {
    const resp: any = await dispatch(
      verifyMailOtp(fieldsData.email.value, fieldsData.emailOtp.value)
    );
    if (resp.result) {
      dispatch(fetchMyInfo(pathname));
      setEmailVerified(true);
    }
  };

  // const purchaseSubscription =
  //   purchaseSubscriptionResponse.user_subscription
  //     ?.external_app_subscriptions[0];

  const activateExternalApp = () => {
    dispatch(
      subscribeToExternalApp(
        purchaseSubscription.activation_url ||
          purchaseSubscription.activation_link,
        goalUID
      )
    ) // @ts-ignore
      .then((result) => {
        if (result) {
          setIsActivated(true);
          onActivationComplete();
        }
      });
  };

  const onStartLearningClick = () => {
    closeModal();
    if (onClickStartLearning) onClickStartLearning();
  };

  const redirectToPlayStore = () => {
    closeModal();
    /* if (
          typeof navigator !== 'undefined' &&
          (navigator.platform === 'iPhone' || navigator.platform === 'iPad')
        ) {
          window.open(externalAppData.external_app.android_url);
        } else {
          window.open(externalAppData.external_app.android_url);
        } */
    window.open(externalAppData.external_app.web_url);
  };

  return (
    <Drawer
      drawerState={showModal}
      onPressBack={closeModal}
      onClose={closeModal}
    >
      <Wrapper>
        {isActivated ? (
          <>
            <ActivatedHeader>
              <H3>
                {`Your
                ${parseInt(`${externalAppData?.duration / 30}`, 10)}
                months PrepLadder subscription is activated!`}
              </H3>
              <CheckCircleFilled size="64px" color="var(--color-i-green)" />
            </ActivatedHeader>
            <EmailDisabledBlock>
              <H6>{me.email}</H6>
            </EmailDisabledBlock>
            <HelpLine>
              {`Use this email id to Login/Signup on ${externalAppData.external_app.display_name}. For questions
              related to your subscription`}
              <ContactText>Contact us</ContactText>
            </HelpLine>
            <ButtonsWrapper>
              <Button
                // @ts-ignore
                label="Start learning"
                onClick={onStartLearningClick}
                dataAnalytics={`${dataAnalytics}-drawer-startLearning`}
              />
              <Button
                // @ts-ignore
                label={`Open ${externalAppData.external_app.display_name}`}
                onClick={redirectToPlayStore}
                variant="hollow"
                color="green"
                dataAnalytics={`${dataAnalytics}-drawer-openApp`}
              />
            </ButtonsWrapper>
          </>
        ) : emailVerified ? (
          <>
            <H2 color="var(--color-text-primary)">Activate subscription</H2>
            <Subtitle>
              We will activate your PrepLadder subscription on this email ID.
            </Subtitle>
            <EmailVerifiedBlock>
              <H6>{me.email}</H6>
              <VerifiedBlock>
                <CheckCircleFilled color="var(--color-i-green)" size="20px" />
                <VerifiedText color="var(--color-i-green)">
                  Verified
                </VerifiedText>
              </VerifiedBlock>
            </EmailVerifiedBlock>
            <Button // @ts-ignore
              label="Activate"
              onClick={activateExternalApp}
            />
          </>
        ) : (
          <EnterEmail
            fieldsData={fieldsData}
            setFieldsData={setFieldsData}
            submitEmail={handleEmailSubmit}
            submitEmailOtp={handleEmailOtpSubmit}
            sendEmailOtpStatus={sendEmailOtpStatus}
            verifyEmailOtpStatus={verifyEmailOtpStatus}
            resetNetworkCallsError={resetNetworkCallsError}
          />
        )}
      </Wrapper>
    </Drawer>
  );
};

export default ExternalAppActivation;
